import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Row,
    Col,
    Button,
    Container,
    FormGroup,
    Label
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import SweetAlert from "react-bootstrap-sweetalert";
import rest from "../../API/api";
import { UserLoginContext } from "../../context/UserLoginContext";
import { authenticatedFetch } from "../../services/authService";

const ViewSubmission = () => {
    const { id } = useParams();
    const [userState] = useContext(UserLoginContext);
    const notificationAlertRef = useRef(null);
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [submission, setSubmission] = useState(null);
    const [waiver, setWaiver] = useState(null);
    const [alert, setalert] = useState(null);

    const notify = (type, message) => {
        const options = {
            place: "tc",
            message: <div>{message}</div>,
            type: type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 5,
        };
        notificationAlertRef.current.notificationAlert(options);
    };

    const hideAlert = () => {
        setalert(null);
    };

    const deleteSubmission = async (submissionId) => {
        try {
            const response = await authenticatedFetch(`${rest.waiversubmissions}/${submissionId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete submission');
            }

            return true;
        } catch (error) {
            console.error('Error deleting submission:', error);
            throw error;
        }
    };

    const deleteSubmissionAlert = () => {
        setalert(
            <SweetAlert
                warning
                style={{ display: "block", marginTop: "-100px" }}
                title="Are you sure?"
                onConfirm={async () => {
                    try {
                        await deleteSubmission(id);
                        hideAlert();
                        notify("success", "Waiver submission has been deleted successfully!");
                        // Redirect back to submissions list after deletion
                        setTimeout(() => {
                            history.push(`/admin/waiversubmissions/${submission.WaiverId}`);
                        }, 1000);
                    } catch (error) {
                        hideAlert();
                        notify("danger", "Failed to delete waiver submission");
                    }
                }}
                onCancel={() => hideAlert()}
                confirmBtnBsStyle="danger"
                cancelBtnBsStyle="default"
                confirmBtnText="Yes, delete it!"
                cancelBtnText="Cancel"
                showCancel
            >
                You will not be able to recover this waiver submission!
            </SweetAlert>
        );
    };

    useEffect(() => {
        const fetchSubmission = async () => {
            try {
                const response = await authenticatedFetch(`${rest.waiversubmissions}/submission/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch submission');
                }

                const data = await response.json();
                setSubmission(data);

                // Fetch the waiver details
                const waiverResponse = await authenticatedFetch(`${rest.waivers}/${data.WaiverId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                });

                if (waiverResponse.ok) {
                    const waiverData = await waiverResponse.json();
                    setWaiver(waiverData);
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching submission:', error);
                notify("danger", "Failed to load submission details");
                setLoading(false);
            }
        };

        if (id && userState.status) {
            fetchSubmission();
        }
    }, [id, userState.status]);

    const isMinor = (dob) => {
        if (!dob) return false;
        const birthDate = new Date(dob);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            return age - 1 < 18;
        }
        return age < 18;
    };

    if (!userState.status) {
        history.push("/admin/dashboard");
        return null;
    }

    if (loading) {
        return (
            <div className="content">
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8}>
                            <Card>
                                <CardBody className="text-center">
                                    <p>Loading submission details...</p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    if (!submission) {
        return (
            <div className="content">
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8}>
                            <Card>
                                <CardBody className="text-center">
                                    <h4>Submission not found</h4>
                                    <Button
                                        color="secondary"
                                        onClick={() => history.push("/admin/waivers")}
                                    >
                                        Back to Waivers
                                    </Button>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <>
            {alert}
            <div className="rna-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
            <div className="content">
                <Container>
                    <Row className="justify-content-center mb-4">
                        <Col md={10} className="text-center">
                            <h2 className="title">Waiver Submission Details</h2>
                            <p className="description">
                                {waiver ? waiver.Title : submission.WaiverTitle} {waiver && waiver.Version && `(v${waiver.Version})`}
                            </p>
                            <p className="text-muted">
                                Submitted on {new Date(submission.SignatureDate).toLocaleString()}
                            </p>
                        </Col>
                    </Row>

                    <Row className="justify-content-center mb-3">
                        <Col md={10}>
                            <Button
                                color="secondary"
                                onClick={() => history.push(`/admin/waiversubmissions/${submission.WaiverId}`)}
                                className="mr-2"
                            >
                                <i className="tim-icons icon-minimal-left"/> Back to Submissions
                            </Button>
                            <Button
                                color="danger"
                                onClick={() => deleteSubmissionAlert()}
                            >
                                <i className="tim-icons icon-trash-simple"/> Delete Submission
                            </Button>
                        </Col>
                    </Row>

                    {/* Waiver Content */}
                    {waiver && (
                        <Row className="justify-content-center mb-4">
                            <Col md={10}>
                                <Card style={{ backgroundColor: '#1e1e2e' }}>
                                    <CardBody>
                                        {waiver.Header1 && (
                                            <h3 className="text-center mb-3" style={{ color: '#ffffff' }}>
                                                {waiver.Header1}
                                            </h3>
                                        )}
                                        {waiver.WaiverText1 && (
                                            <p style={{
                                                color: '#e0e0e0',
                                                whiteSpace: 'pre-wrap',
                                                textAlign: 'justify',
                                                lineHeight: '1.8'
                                            }}>
                                                {waiver.WaiverText1}
                                            </p>
                                        )}
                                        {waiver.Header2 && (
                                            <h3 className="text-center mb-3 mt-4" style={{ color: '#ffffff' }}>
                                                {waiver.Header2}
                                            </h3>
                                        )}
                                        {waiver.WaiverText2 && (
                                            <p style={{
                                                color: '#e0e0e0',
                                                whiteSpace: 'pre-wrap',
                                                textAlign: 'justify',
                                                lineHeight: '1.8'
                                            }}>
                                                {waiver.WaiverText2}
                                            </p>
                                        )}
                                        {waiver.Header3 && (
                                            <h3 className="text-center mb-3 mt-4" style={{ color: '#ffffff' }}>
                                                {waiver.Header3}
                                            </h3>
                                        )}
                                        {waiver.WaiverText3 && (
                                            <p style={{
                                                color: '#e0e0e0',
                                                whiteSpace: 'pre-wrap',
                                                textAlign: 'justify',
                                                lineHeight: '1.8'
                                            }}>
                                                {waiver.WaiverText3}
                                            </p>
                                        )}
                                        {waiver.ImageUrl && (
                                            <div className="mt-4 text-center" style={{
                                                padding: '20px',
                                                backgroundColor: 'white',
                                                borderRadius: '4px'
                                            }}>
                                                <img
                                                    src={waiver.ImageUrl}
                                                    alt={waiver.Title}
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '400px',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Participant Information */}
                    <Row className="justify-content-center mb-4">
                        <Col md={10}>
                            <Card style={{ backgroundColor: '#27293d' }}>
                                <CardHeader>
                                    <CardTitle tag="h4">Participant Information</CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>First Name</Label>
                                                <p className="form-control-static">{submission.FirstName}</p>
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Middle Name</Label>
                                                <p className="form-control-static">{submission.MiddleName || 'N/A'}</p>
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Last Name</Label>
                                                <p className="form-control-static">{submission.LastName}</p>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Email</Label>
                                                <p className="form-control-static">{submission.Email}</p>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Phone</Label>
                                                <p className="form-control-static">{submission.Phone || 'N/A'}</p>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Date of Birth</Label>
                                                <p className="form-control-static">
                                                    {submission.DOB ? new Date(submission.DOB).toLocaleDateString() : 'N/A'}
                                                    {submission.DOB && isMinor(submission.DOB) && (
                                                        <span className="badge badge-warning ml-2">Minor</span>
                                                    )}
                                                </p>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Email Opt-In</Label>
                                                <p className="form-control-static">
                                                    {submission.EmailOptIn ? 'Yes' : 'No'}
                                                </p>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Address Information */}
                    {(submission.AddressLine1 || submission.City || submission.State) && (
                        <Row className="justify-content-center mb-4">
                            <Col md={10}>
                                <Card style={{ backgroundColor: '#27293d' }}>
                                    <CardHeader>
                                        <CardTitle tag="h4">Address Information</CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label>Address Line 1</Label>
                                                    <p className="form-control-static">{submission.AddressLine1 || 'N/A'}</p>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        {submission.AddressLine2 && (
                                            <Row>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label>Address Line 2</Label>
                                                        <p className="form-control-static">{submission.AddressLine2}</p>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        )}
                                        <Row>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label>City</Label>
                                                    <p className="form-control-static">{submission.City || 'N/A'}</p>
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label>State</Label>
                                                    <p className="form-control-static">{submission.State || 'N/A'}</p>
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label>Zip Code</Label>
                                                    <p className="form-control-static">{submission.ZipCode || 'N/A'}</p>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label>Country</Label>
                                                    <p className="form-control-static">{submission.Country || 'N/A'}</p>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Emergency Contact */}
                    <Row className="justify-content-center mb-4">
                        <Col md={10}>
                            <Card style={{ backgroundColor: '#2b3553' }}>
                                <CardHeader>
                                    <CardTitle tag="h4">Emergency Contact</CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>First Name</Label>
                                                <p className="form-control-static">{submission.EmergencyContactFirstName || 'N/A'}</p>
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Last Name</Label>
                                                <p className="form-control-static">{submission.EmergencyContactLastName || 'N/A'}</p>
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Phone</Label>
                                                <p className="form-control-static">{submission.EmergencyContactPhone || 'N/A'}</p>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Parent/Guardian Information (if minor) */}
                    {submission.DOB && isMinor(submission.DOB) && (submission.ParentGuardianFirstName || submission.ParentGuardianLastName) && (
                        <Row className="justify-content-center mb-4">
                            <Col md={10}>
                                <Card style={{ backgroundColor: '#3a4561' }}>
                                    <CardHeader>
                                        <CardTitle tag="h4">Parent/Guardian Information</CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label>First Name</Label>
                                                    <p className="form-control-static">{submission.ParentGuardianFirstName || 'N/A'}</p>
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label>Last Name</Label>
                                                    <p className="form-control-static">{submission.ParentGuardianLastName || 'N/A'}</p>
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label>Date of Birth</Label>
                                                    <p className="form-control-static">
                                                        {submission.ParentGuardianDOB ? new Date(submission.ParentGuardianDOB).toLocaleDateString() : 'N/A'}
                                                    </p>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        {submission.ParentGuardianSignature && (
                                            <Row>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Parent / Guardian Acknowledgment (Required for Minors)</Label>
                                                        <p style={{ fontSize: '0.9rem', marginTop: '10px', marginBottom: '15px', color: '#e0e0e0' }}>
                                                            I am the legal parent/guardian of the minor participant. I acknowledge that I am waiving and releasing the minor's rights and my own rights to the fullest extent permitted by Colorado law. I agree to all terms of this Agreement on behalf of the minor.
                                                        </p>
                                                        <Label style={{ marginTop: '10px' }}>Parent/Guardian Signature</Label>
                                                        {(submission.ParentGuardianSignature.startsWith('http') || submission.ParentGuardianSignature.startsWith('data:')) ? (
                                                            <div style={{
                                                                backgroundColor: 'white',
                                                                padding: '10px',
                                                                borderRadius: '4px',
                                                                display: 'inline-block',
                                                                marginTop: '10px'
                                                            }}>
                                                                <img
                                                                    src={submission.ParentGuardianSignature}
                                                                    alt="Parent/Guardian Signature"
                                                                    style={{
                                                                        maxWidth: '300px',
                                                                        maxHeight: '150px',
                                                                        display: 'block'
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <p className="form-control-static" style={{ fontStyle: 'italic' }}>
                                                                {submission.ParentGuardianSignature}
                                                            </p>
                                                        )}
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Signature and Consent */}
                    <Row className="justify-content-center mb-4">
                        <Col md={10}>
                            <Card style={{ backgroundColor: '#4a5568' }}>
                                <CardHeader>
                                    <CardTitle tag="h4">Signature and Consent</CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Participant (Age 18 or older)</Label>
                                                <p style={{ fontSize: '0.9rem', marginTop: '10px', marginBottom: '15px', color: '#e0e0e0' }}>
                                                    I have read and understand this Agreement. I sign voluntarily.
                                                </p>
                                                <Label style={{ marginTop: '10px' }}>Participant Signature</Label>
                                                {(submission.Signature.startsWith('http') || submission.Signature.startsWith('data:')) ? (
                                                    <div style={{
                                                        backgroundColor: 'white',
                                                        padding: '10px',
                                                        borderRadius: '4px',
                                                        display: 'inline-block',
                                                        marginTop: '10px'
                                                    }}>
                                                        <img
                                                            src={submission.Signature}
                                                            alt="Participant Signature"
                                                            style={{
                                                                maxWidth: '300px',
                                                                maxHeight: '150px',
                                                                display: 'block'
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <p className="form-control-static" style={{ fontStyle: 'italic' }}>
                                                        {submission.Signature}
                                                    </p>
                                                )}
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row className="mt-4">
                                        <Col md={12}>
                                            <div style={{
                                                padding: '15px',
                                                backgroundColor: 'rgba(29, 140, 248, 0.1)',
                                                border: '2px solid #1d8cf8',
                                                borderRadius: '4px',
                                                marginBottom: '15px'
                                            }}>
                                                <Label style={{ fontWeight: 'bold', color: '#1d8cf8', fontSize: '1rem' }}>Colorado Inherent Risk Acknowledgment</Label>
                                                <p style={{ fontSize: '0.9rem', marginBottom: '0', color: '#e0e0e0', marginTop: '5px' }}>
                                                    I acknowledge that under Colorado law, participants in recreational and athletic activities assume the inherent risks of such activities. This Agreement is intended to be interpreted and enforced consistent with that principle and Colorado public policy.
                                                </p>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Signature Date</Label>
                                                <p className="form-control-static">
                                                    {new Date(submission.SignatureDate).toLocaleString()}
                                                </p>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Electronic Signature Consent</Label>
                                                <p className="form-control-static">
                                                    {submission.ElectronicSignatureConsent ? 'Yes' : 'No'}
                                                </p>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Metadata */}
                    {(submission.IPAddress || submission.UserAgent) && (
                        <Row className="justify-content-center mb-4">
                            <Col md={10}>
                                <Card style={{ backgroundColor: '#1a1d2e' }}>
                                    <CardHeader>
                                        <CardTitle tag="h4">Submission Metadata</CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        {submission.IPAddress && (
                                            <Row>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label>IP Address</Label>
                                                        <p className="form-control-static">{submission.IPAddress}</p>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        )}
                                        {submission.UserAgent && (
                                            <Row>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label>User Agent</Label>
                                                        <p className="form-control-static" style={{ fontSize: '0.85rem' }}>
                                                            {submission.UserAgent}
                                                        </p>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    <Row className="justify-content-center mb-5">
                        <Col md={10}>
                            <Button
                                color="secondary"
                                onClick={() => history.push(`/admin/waiversubmissions/${submission.WaiverId}`)}
                                block
                            >
                                <i className="tim-icons icon-minimal-left"/> Back to Submissions
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
};

export default ViewSubmission;
